import React, { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import ModalBackdrop from "@/components/ModalBackdrop";
import { ContextSelect } from "./ContextSelect";
import { ValidityFields, metaPatch } from "./ValidityFields";
import { useContextVocab } from "./vocab";

const MODES = [
    ["add", "Add", "keep existing contexts, add the selected ones"],
    ["remove", "Remove", "take the selected contexts away"],
    ["replace", "Replace", "selected contexts become the only ones"],
];

export function BulkMetadataModal({ items, onClose, onDone, endpoint = "/admin/datalake/items/bulk-metadata" }) {
    const { active, irbRelevant } = useContextVocab();
    const [contexts, setContexts] = useState([]);
    const [mode, setMode] = useState("add");
    const [meta, setMeta] = useState({});
    const [saving, setSaving] = useState(false);
    const patch = metaPatch(meta, {});
    const touchesContexts = contexts.length > 0;
    const touchesMeta = Object.keys(patch).length > 0;
    const irbItems = items.filter((i) => irbRelevant(i.contexts)).length;
    const showIrb = (mode !== "remove" && irbRelevant(contexts)) || irbItems > 0;

    const apply = async () => {
        if (!touchesContexts && !touchesMeta) { toast.info("Pick contexts and/or fill at least one metadata field"); return; }
        setSaving(true);
        try {
            const body = { item_ids: items.map((i) => i.id) };
            if (touchesContexts) { body.contexts = contexts; body.contexts_mode = mode; }
            if (touchesMeta) body.doc_meta = patch;
            const { data } = await api.post(endpoint, body);
            toast.success(`${data.updated} of ${data.requested} documents updated${data.unchanged ? ` · ${data.unchanged} unchanged` : ""}${data.irb_skipped ? ` · IRB fields skipped on ${data.irb_skipped} non-IRB document${data.irb_skipped === 1 ? "" : "s"}` : ""}${data.forbidden ? ` · ${data.forbidden} skipped (no access)` : ""}`);
            onDone(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <ModalBackdrop onClose={onClose}>
            <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0a0a0a] border border-white/15 rounded-md" onClick={(e) => e.stopPropagation()} data-testid="dl-bulk-modal">
                <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3 sticky top-0 bg-[#0a0a0a] z-10">
                    <div>
                        <div className="font-display text-lg tracking-tight">Bulk edit · {items.length} document{items.length === 1 ? "" : "s"}</div>
                        <div className="text-[11px] text-white/45 font-mono truncate max-w-[460px]" data-testid="dl-bulk-titles">{items.slice(0, 3).map((i) => i.document_name || i.title || i.id).join(" · ")}{items.length > 3 ? ` · +${items.length - 3} more` : ""}</div>
                    </div>
                    <button onClick={onClose} className="ml-auto text-white/60 hover:text-white" data-testid="dl-bulk-close"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5 space-y-4">
                    <div className="flex items-center gap-1.5" data-testid="dl-bulk-mode">
                        {MODES.map(([k, l, d]) => (
                            <button key={k} type="button" onClick={() => setMode(k)} title={d} data-testid={`dl-bulk-mode-${k}`} aria-pressed={mode === k}
                                className={`px-2.5 py-1 rounded-sm border text-[11px] font-mono ${mode === k ? "border-[#7C3AED]/60 bg-[#7C3AED]/15 text-[#c7a6ff]" : "border-white/10 text-white/50 hover:bg-white/5"}`}>{l}</button>
                        ))}
                        <span className="text-[11px] text-white/40 ml-2">{MODES.find((m) => m[0] === mode)[2]}; typed documents keep their own context.</span>
                    </div>
                    <ContextSelect value={contexts} onChange={setContexts} options={active} testId="dl-bulk-contexts" />
                    <ValidityFields value={meta} onChange={setMeta} testId="dl-bulk-validity" showIrb={showIrb}
                        irbHint="None of the selected documents is IRB-relevant (ICF, protocol, regulatory, safety), so IRB approval fields are not offered." />
                    {showIrb && irbItems < items.length && (
                        <div className="text-[11px] text-amber-100/70" data-testid="dl-bulk-irb-partial">IRB fields will be applied to the {irbItems} IRB-relevant document{irbItems === 1 ? "" : "s"} only — budgets, CTAs and manuals never receive them.</div>
                    )}
                    <div className="text-[11px] text-white/40" data-testid="dl-bulk-hint">Only the fields you fill in are applied to every selected document — blank fields stay as they are. Each change is audited per document.</div>
                    <div className="flex justify-end gap-2 pt-1">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-white/15 rounded-md hover:bg-white/5">Cancel</button>
                        <button type="button" onClick={apply} disabled={saving || (!touchesContexts && !touchesMeta)} data-testid="dl-bulk-apply"
                            className="px-4 py-2 bg-[#7C3AED] text-white rounded-md text-sm font-medium hover:bg-[#7C3AED]/90 disabled:opacity-40">
                            {saving ? "Applying…" : `Apply to ${items.length}`}
                        </button>
                    </div>
                </div>
            </div>
        </ModalBackdrop>
    );
}
